import React, { forwardRef } from "react";
import clsx from "clsx";

const sizes = {
  sm: {
    switch: "w-8 h-4",
    thumb: "w-3 h-3",
    translate: "translate-x-4",
  },
  md: {
    switch: "w-11 h-6",
    thumb: "w-5 h-5",
    translate: "translate-x-5",
  },
  lg: {
    switch: "w-14 h-7",
    thumb: "w-6 h-6",
    translate: "translate-x-7",
  },
};

const Switch = forwardRef(({
  label,
  description,
  checked = false,
  onChange,
  disabled = false,
  size = "md",
  color = "primary",
  className = "",
  id,
  ...props
}, ref) => {
  const switchId = id || `switch-${Math.random().toString(36).substr(2, 9)}`;
  const sizeConfig = sizes[size];

  const colorClasses = {
    primary: "bg-primary-500",
    success: "bg-success-500",
    danger: "bg-danger-500",
    warning: "bg-warning-500",
  };

  const handleChange = (e) => {
    if (onChange) {
      onChange(e.target.checked, e);
    }
  };

  return (
    <div className={clsx("flex items-center", className)}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={label ? `${switchId}-label` : undefined}
        disabled={disabled}
        onClick={() => !disabled && onChange && onChange(!checked)}
        className={clsx(
          "relative inline-flex flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out",
          "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-dark-900",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          sizeConfig.switch,
          checked ? colorClasses[color] : "bg-dark-300 dark:bg-dark-600"
        )}
      >
        <span
          className={clsx(
            "pointer-events-none inline-block rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out",
            sizeConfig.thumb,
            checked ? sizeConfig.translate : "translate-x-0"
          )}
        />
      </button>
      <input
        ref={ref}
        type="checkbox"
        id={switchId}
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        className="sr-only"
        {...props}
      />
      {(label || description) && (
        <div className="ml-3">
          {label && (
            <label
              id={`${switchId}-label`}
              htmlFor={switchId}
              className={clsx(
                "text-sm font-medium",
                disabled
                  ? "text-dark-400 dark:text-dark-500 cursor-not-allowed"
                  : "text-dark-700 dark:text-dark-300 cursor-pointer"
              )}
            >
              {label}
            </label>
          )}
          {description && (
            <p className="text-xs text-dark-500 dark:text-dark-400">
              {description}
            </p>
          )}
        </div>
      )}
    </div>
  );
});

Switch.displayName = "Switch";

export default Switch;
